import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import {
  Monitor, Download, Link2, Unlink, RefreshCw,
  Mic, Eye, AppWindow, BarChart3, Shield, Clock, ChevronDown,
  ChevronUp, Copy, Check, Loader2, Wifi, WifiOff, Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  nl: {
    title: 'Desktop Probe',
    subtitle: 'Verbind Emma met je Windows desktop voor spraakcommando\'s, slimme suggesties en snelle acties.',
    back: 'Terug naar Portaal',
    // Download section
    downloadTitle: 'Emma Desktop Probe downloaden',
    downloadDesc: 'Lichtgewicht Windows-app (~5MB) die Emma uitbreidt naar je bureaublad. Draait stil op de achtergrond.',
    downloadBtn: 'Download voor Windows',
    requirements: 'Windows 10/11, 64-bit',
    version: 'Versie',
    // Pairing section
    pairTitle: 'Apparaat koppelen',
    pairDesc: 'Genereer een koppelcode en voer deze in op je Desktop Probe app.',
    generateCode: 'Genereer Koppelcode',
    codeLabel: 'Koppelcode',
    codeExpires: 'Geldig voor 5 minuten',
    codeCopied: 'Gekopieerd!',
    codeInstruction: 'Open de Emma Desktop Probe op je PC en voer deze code in.',
    // Devices section
    devicesTitle: 'Gekoppelde Apparaten',
    noDevices: 'Nog geen apparaten gekoppeld — koppel er één om te beginnen.',
    noDevicesDesc: 'Download de Emma Desktop Probe en koppel je eerste apparaat.',
    online: 'Online',
    offline: 'Offline',
    paired: 'Gekoppeld',
    lastSeen: 'Laatst actief',
    unpair: 'Ontkoppelen',
    unpairConfirm: 'Weet je zeker dat je dit apparaat wilt ontkoppelen?',
    // Permissions
    permTitle: 'Permissies',
    permVoice: 'Spraakcommando\'s',
    permVoiceDesc: 'Emma kan luisteren via push-to-talk',
    permAwareness: 'Applicatie-awareness',
    permAwarenessDesc: 'Emma ziet welke app je gebruikt (alleen naam, geen inhoud)',
    permApps: 'Apps en sites openen',
    permAppsDesc: 'Emma kan apps en websites voor je openen',
    permAnalytics: 'Gebruiksanalyse',
    permAnalyticsDesc: 'Emma analyseert app-gebruik voor productiviteitstips',
    // Audit
    auditTitle: 'Activiteitenlog',
    auditEmpty: 'Geen activiteiten geregistreerd',
    auditShowMore: 'Meer tonen',
    // Features
    features: [
      { icon: 'mic', title: 'Spraakcommando\'s', desc: 'Praat met Emma via push-to-talk' },
      { icon: 'app', title: 'Snelle acties', desc: 'Open apps, sites en Krypton9 tools' },
      { icon: 'eye', title: 'Slim bewustzijn', desc: 'Emma begrijpt welke app je gebruikt' },
      { icon: 'shield', title: 'Privacy-first', desc: 'Jij bepaalt exact wat Emma mag zien' },
    ],
  },
  en: {
    title: 'Desktop Probe',
    subtitle: 'Connect Emma to your Windows desktop for voice commands, smart suggestions and quick actions.',
    back: 'Back to Portal',
    downloadTitle: 'Download Emma Desktop Probe',
    downloadDesc: 'Lightweight Windows app (~5MB) that extends Emma to your desktop. Runs quietly in the background.',
    downloadBtn: 'Download for Windows',
    requirements: 'Windows 10/11, 64-bit',
    version: 'Version',
    pairTitle: 'Pair Device',
    pairDesc: 'Generate a pairing code and enter it in your Desktop Probe app.',
    generateCode: 'Generate Pairing Code',
    codeLabel: 'Pairing Code',
    codeExpires: 'Valid for 5 minutes',
    codeCopied: 'Copied!',
    codeInstruction: 'Open Emma Desktop Probe on your PC and enter this code.',
    devicesTitle: 'Paired Devices',
    noDevices: 'No devices paired yet — connect one to get started.',
    noDevicesDesc: 'Download the Emma Desktop Probe and pair your first device.',
    online: 'Online',
    offline: 'Offline',
    paired: 'Paired',
    lastSeen: 'Last active',
    unpair: 'Unpair',
    unpairConfirm: 'Are you sure you want to unpair this device?',
    permTitle: 'Permissions',
    permVoice: 'Voice commands',
    permVoiceDesc: 'Emma can listen via push-to-talk',
    permAwareness: 'Application awareness',
    permAwarenessDesc: 'Emma sees which app you use (name only, no content)',
    permApps: 'Open apps and sites',
    permAppsDesc: 'Emma can open apps and websites for you',
    permAnalytics: 'Usage analytics',
    permAnalyticsDesc: 'Emma analyzes app usage for productivity tips',
    auditTitle: 'Activity Log',
    auditEmpty: 'No activities recorded',
    auditShowMore: 'Show more',
    features: [
      { icon: 'mic', title: 'Voice Commands', desc: 'Talk to Emma via push-to-talk' },
      { icon: 'app', title: 'Quick Actions', desc: 'Open apps, sites and Krypton9 tools' },
      { icon: 'eye', title: 'Smart Awareness', desc: 'Emma understands which app you use' },
      { icon: 'shield', title: 'Privacy-first', desc: 'You control exactly what Emma can see' },
    ],
  },
};

const FEATURE_ICONS = { mic: Mic, app: AppWindow, eye: Eye, shield: Shield };

const AUDIT_LABELS = {
  device_paired: { nl: 'Apparaat gekoppeld', en: 'Device paired' },
  device_unpaired: { nl: 'Apparaat ontkoppeld', en: 'Device unpaired' },
  action_executed: { nl: 'Actie uitgevoerd', en: 'Action executed' },
  action_result: { nl: 'Actie resultaat', en: 'Action result' },
  voice_command: { nl: 'Spraakcommando', en: 'Voice command' },
  permission_changed: { nl: 'Permissie gewijzigd', en: 'Permission changed' },
  connection_opened: { nl: 'Verbinding geopend', en: 'Connection opened' },
  connection_lost: { nl: 'Verbinding verbroken', en: 'Connection lost' },
};

const DesktopProbe = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = T[lang] || T.nl;
  const headers = { Authorization: `Bearer ${token}` };

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pairingCode, setPairingCode] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [expandedDevice, setExpandedDevice] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [releaseInfo, setReleaseInfo] = useState(null);

  useEffect(() => {
    const loadRelease = async () => {
      try {
        const res = await axios.get(`${API}/probe/releases/latest`);
        setReleaseInfo(res.data);
      } catch (_) {}
    };
    loadRelease();
  }, []);

  const fetchDevices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/probe/devices`, { headers });
      setDevices(res.data.devices || []);
    } catch (_) {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  const generateCode = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/probe/pair`, { language: lang }, { headers });
      setPairingCode(res.data.pairing_code);
      setTimeout(() => setPairingCode(null), 300000); // Expire after 5 min
    } catch (_) {}
    setGenerating(false);
  };

  const copyCode = () => {
    if (pairingCode) {
      navigator.clipboard.writeText(pairingCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const unpairDevice = async (deviceId) => {
    if (!window.confirm(t.unpairConfirm)) return;
    try {
      await axios.delete(`${API}/probe/devices/${deviceId}`, { headers });
      fetchDevices();
    } catch (_) {}
  };

  const loadPermissions = async (deviceId) => {
    try {
      const res = await axios.get(`${API}/probe/permissions/${deviceId}`, { headers });
      setPermissions(prev => ({ ...prev, [deviceId]: res.data }));
    } catch (_) {}
  };

  const togglePermission = async (deviceId, scope, value) => {
    try {
      await axios.put(`${API}/probe/permissions/${deviceId}`, { [scope]: value }, { headers });
      setPermissions(prev => ({ ...prev, [deviceId]: { ...prev[deviceId], [scope]: value } }));
    } catch (_) {}
  };

  const loadAudit = async () => {
    try {
      const res = await axios.get(`${API}/probe/audit?limit=50`, { headers });
      setAuditLogs(res.data.logs || []);
    } catch (_) {}
  };

  const toggleDevice = (deviceId) => {
    if (expandedDevice === deviceId) {
      setExpandedDevice(null);
    } else {
      setExpandedDevice(deviceId);
      if (!permissions[deviceId]) loadPermissions(deviceId);
    }
  };

  const permScopes = [
    { key: 'voice_commands', label: t.permVoice, desc: t.permVoiceDesc, icon: Mic },
    { key: 'app_awareness', label: t.permAwareness, desc: t.permAwarenessDesc, icon: Eye },
    { key: 'open_apps', label: t.permApps, desc: t.permAppsDesc, icon: AppWindow },
    { key: 'usage_analytics', label: t.permAnalytics, desc: t.permAnalyticsDesc, icon: BarChart3 },
  ];

  return (
    <div className="tool-page" data-testid="desktop-probe-page">
      <div className="tool-header">
        <h1 className="heading-1" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Monitor size={24} /> {t.title}
        </h1>
      </div>
      <p className="body-medium" style={{ color: '#666', marginBottom: '2rem', maxWidth: 640 }}>{t.subtitle}</p>

      {/* Feature highlights */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '2rem' }}>
        {(Array.isArray(t.features) ? t.features : []).map((f, i) => {
          const Icon = FEATURE_ICONS[f.icon];
          return (
            <div key={i} style={{
              background: '#16202C', border: '1px solid #e2e8f0', borderRadius: 10,
              padding: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
            }}>
              <div style={{ background: '#16202C', borderRadius: 8, padding: 8, flexShrink: 0 }}>
                <Icon size={18} style={{ color: '#3b82f6' }} />
              </div>
              <div>
                <p style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 2 }}>{f.title}</p>
                <p style={{ fontSize: '0.78rem', color: '#888' }}>{f.desc}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Download section */}
      <div data-testid="probe-download" style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: 14,
        padding: '1.75rem 2rem', marginBottom: '1.5rem', color: '#fff',
      }}>
        <h2 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '0.4rem' }}>{t.downloadTitle}</h2>
        <p style={{ fontSize: '0.88rem', color: '#94a3b8', marginBottom: '1.25rem', maxWidth: 500 }}>{t.downloadDesc}</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          {releaseInfo?.available ? (
            <button data-testid="download-probe-btn" onClick={() => {
              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/probe/releases/download/latest`, '_blank');
            }} style={{
              background: '#3b82f6', border: 'none', borderRadius: 8, padding: '0.65rem 1.5rem',
              color: '#fff', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <Download size={18} /> {t.downloadBtn}
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button disabled style={{
                background: '#334155', border: 'none', borderRadius: 8, padding: '0.65rem 1.5rem',
                color: '#64748b', fontWeight: 600, fontSize: '0.9rem', cursor: 'not-allowed',
                display: 'flex', alignItems: 'center', gap: 8, opacity: 0.7,
              }}>
                <Download size={18} /> {t.downloadBtn}
              </button>
              <span style={{ fontSize: '0.78rem', color: '#f59e0b', background: 'rgba(245,158,11,0.1)', padding: '4px 10px', borderRadius: 6 }}>
                {lang === 'nl' ? 'Binnenkort beschikbaar' : 'Coming soon'}
              </span>
            </div>
          )}
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
            {t.requirements} &middot; {t.version} {releaseInfo?.version || '1.0.0'}
            {releaseInfo?.size_mb > 0 && ` \u00b7 ${releaseInfo.size_mb} MB`}
          </span>
        </div>
        {releaseInfo?.changelog && releaseInfo?.available && (
          <p style={{ fontSize: '0.75rem', color: '#475569', marginTop: '0.75rem', fontStyle: 'italic' }}>
            {releaseInfo.changelog}
          </p>
        )}
      </div>

      {/* Pairing section */}
      <div data-testid="probe-pairing" style={{
        background: '#0E1620', border: '1px solid #e2e8f0', borderRadius: 14,
        padding: '1.5rem 1.75rem', marginBottom: '1.5rem',
      }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.3rem', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Link2 size={18} /> {t.pairTitle}
        </h2>
        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>{t.pairDesc}</p>

        {!pairingCode ? (
          <button onClick={generateCode} disabled={generating} data-testid="generate-code-btn"
            className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {generating ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={16} />}
            {t.generateCode}
          </button>
        ) : (
          <div data-testid="pairing-code-display" style={{
            background: '#16202C', border: '2px solid #86efac', borderRadius: 12,
            padding: '1.25rem', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap',
          }}>
            <div>
              <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#15803d', marginBottom: 4 }}>{t.codeLabel}</p>
              <div style={{ display: 'flex', gap: 6 }}>
                {pairingCode.split('').map((digit, i) => (
                  <div key={i} style={{
                    width: 42, height: 50, background: '#0E1620', border: '2px solid #16a34a',
                    borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.5rem', fontWeight: 700, color: '#166534', fontFamily: 'monospace',
                  }}>{digit}</div>
                ))}
              </div>
              <p style={{ fontSize: '0.75rem', color: '#888', marginTop: 6 }}>{t.codeExpires}</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button onClick={copyCode} data-testid="copy-code-btn" style={{
                background: '#0E1620', border: '1px solid #d1d5db', borderRadius: 6,
                padding: '0.4rem 0.8rem', fontSize: '0.8rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                {codeCopied ? <Check size={14} style={{ color: '#16a34a' }} /> : <Copy size={14} />}
                {codeCopied ? t.codeCopied : 'Kopieer'}
              </button>
              <p style={{ fontSize: '0.72rem', color: '#666', maxWidth: 200 }}>{t.codeInstruction}</p>
            </div>
          </div>
        )}
      </div>

      {/* Devices section */}
      <div data-testid="probe-devices" style={{
        background: '#0E1620', border: '1px solid #e2e8f0', borderRadius: 14,
        padding: '1.5rem 1.75rem', marginBottom: '1.5rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Monitor size={18} /> {t.devicesTitle}
          </h2>
          <button onClick={fetchDevices} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: 4,
          }}><RefreshCw size={16} /></button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#888' }} />
          </div>
        ) : devices.length === 0 ? (
          <div data-testid="no-devices" style={{
            textAlign: 'center', padding: '2rem', background: '#16202C', borderRadius: 10,
            border: '1px dashed #ddd',
          }}>
            <Monitor size={32} style={{ color: '#ccc', marginBottom: 8 }} />
            <p style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: 4 }}>{t.noDevices}</p>
            <p style={{ fontSize: '0.82rem', color: '#888' }}>{t.noDevicesDesc}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {(Array.isArray(devices) ? devices : []).map((device) => (
              <div key={device.id} data-testid={`device-${device.id}`} style={{
                border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden',
              }}>
                {/* Device header */}
                <div onClick={() => toggleDevice(device.id)} style={{
                  padding: '0.85rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
                  cursor: 'pointer', background: expandedDevice === device.id ? '#16202C' : '#16202C',
                }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: device.status === 'online' ? '#081F0F' : '#16202C',
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{device.device_name}</span>
                      <span style={{
                        fontSize: '0.7rem', padding: '1px 8px', borderRadius: 4, fontWeight: 600,
                        background: device.status === 'online' ? '#16202C' : '#16202C',
                        color: device.status === 'online' ? '#15803d' : '#888',
                      }}>
                        {device.status === 'online' ? t.online : t.offline}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#999', marginTop: 2 }}>
                      {device.os_version && <span>{device.os_version} &middot; </span>}
                      v{device.probe_version} &middot; {t.paired}: {new Date(device.paired_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); unpairDevice(device.id); }}
                    data-testid={`unpair-${device.id}`}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 6 }}>
                    <Unlink size={16} />
                  </button>
                  {expandedDevice === device.id ? <ChevronUp size={16} color="#888" /> : <ChevronDown size={16} color="#888" />}
                </div>

                {/* Expanded: Permissions */}
                {expandedDevice === device.id && permissions[device.id] && (
                  <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #f0f0f0', background: '#16202C' }}>
                    <h3 style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.75rem', color: '#F4F7FB' }}>{t.permTitle}</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                      {(Array.isArray(permScopes) ? permScopes : []).map(({ key, label, desc, icon: ScopeIcon }) => (
                        <div key={key} style={{
                          display: 'flex', alignItems: 'center', gap: '0.75rem',
                          padding: '0.5rem 0',
                        }}>
                          <ScopeIcon size={16} style={{ color: '#64748b', flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: '0.85rem', fontWeight: 500 }}>{label}</p>
                            <p style={{ fontSize: '0.72rem', color: '#888' }}>{desc}</p>
                          </div>
                          <label className="toggle-switch" style={{ flexShrink: 0 }}>
                            <input type="checkbox" checked={permissions[device.id]?.[key] || false}
                              data-testid={`perm-${key}-${device.id}`}
                              onChange={(e) => togglePermission(device.id, key, e.target.checked)} />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Audit log section */}
      <div data-testid="probe-audit" style={{
        background: '#0E1620', border: '1px solid #e2e8f0', borderRadius: 14,
        padding: '1.5rem 1.75rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Clock size={18} /> {t.auditTitle}
          </h2>
          <button onClick={() => { setShowAudit(!showAudit); if (!showAudit) loadAudit(); }}
            data-testid="toggle-audit-btn" style={{
              background: 'none', border: '1px solid #ddd', borderRadius: 6,
              padding: '0.3rem 0.7rem', fontSize: '0.8rem', cursor: 'pointer', color: '#555',
            }}>
            {showAudit ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>

        {showAudit && (
          auditLogs.length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: '#888', padding: '1rem 0' }}>{t.auditEmpty}</p>
          ) : (
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              <table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: '#555' }}>Tijd</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: '#555' }}>Event</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: '#555' }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(auditLogs) ? auditLogs : []).map((log, i) => (
                    <tr key={log.id || i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '6px 8px', color: '#888', whiteSpace: 'nowrap' }}>
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        {(AUDIT_LABELS[log.event_type] || {})[lang] || log.event_type}
                      </td>
                      <td style={{ padding: '6px 8px', color: '#888', fontFamily: 'monospace', fontSize: '0.72rem' }}>
                        {log.action && `${log.action} `}
                        {log.params && Object.keys(log.params).length > 0 && JSON.stringify(log.params).slice(0, 80)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default DesktopProbe;
